import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class EconomyService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves list of activity statuses using the given parameters.
     *
     * @param params the configuration for the list of activitystatuses to retrieve.
     * @returns a promise containing a list of activity statuses.
     */
    public getActivityStatuses(params?: Partial<ActivityStatusesParams>): Promise<ActivityStatusItem[]> {
        return this.datatask.Post(50, params);
    }

    /**
     * Retrieves list of all activity types.
     *
     * @datatask 49
     * @datataskName sp_ws_p2_activitytype_list
     * @returns promise containing list of activitytypes.
     */
    public getActivityTypes(params?: Partial<ActivityTypeParams>): Promise<ActivityTypeItem[]> {
        return this.datatask.Post(49, params);
    }

    public getBusinessSites(params?: BusinessSitesParams): Promise<Item[]> {
        return this.datatask.Post(1655, params || {});
    }

    /**
     * Retrieves list of valutas.
     *
     * @datatask 373
     * @datataskName sp_ws_p2_valuta_list
     * @returns a promise containing a list of valutas.
     */
    public getValutas(): Promise<ValutaItem[]> {
        return this.datatask.Post(373);
    }

    /**
     * Retrieves list of different shift types.
     *
     * @param params configuration of the list.
     * @datatask 516
     * @datataskName sp_ws_p2_shift_list
     * @returns a promise containing the list of shift types.
     */
    public getShiftTypes(params?: ShiftTypesParams): Promise<ShiftTypeItem[]> {
        return this.datatask.Post(516, params);
    }

    /**
     * Retrieves priceslists.
     *
     * @param params configuration of the list to retrieve.
     * @datatask 627
     * @datataskName sp_ws_p2_pricelist_list
     * @returns a promise containing the pricelists.
     */
    public getPriceLists(params?: PriceListsParams): Promise<PriceListItem[]> {
        return this.datatask.Post(627, params);
    }

    /**
     * Retrieves list of payment types.
     *
     * @datatask 624
     * @datataskName sp_ws_p2_payment_list
     * @return a promise containing list of payment types.
     */
    public getPaymentTypes(): Promise<PaymentTypeItem[]> {
        return this.datatask.Post(624);
    }

    /**
     * Retireves list of different taxes.
     *
     * @datatask 640
     * @datataskName sp_ws_p2_tax_list
     * @return a promise containing list of different tax types.
     */
    public getTaxes(params?: TaxesParams): Promise<TaxItem[]> {
        return this.datatask.Post(640, params);
    }

    /**
     * Retrieves the tax for the given tax params.
     *
     * @param params the params to get tax from.
     * @datatask 732
     * @datataskName sp_ws_p2_tax_get
     * @returns a promise containing the tax info for the tax keyno given.
     */
    public async getTax(params: TaxParams): Promise<Tax> {
        return (await this.datatask.Post(732, params))[0];
    }

    /**
     * Retrieves customer persons for the given cust no.
     *
     * @datatask 71
     * @datataskName sp_ws_p2_customer_persons_list
     * @param params the params containing the cust no to get customer persons for.
     * @returns a promise containing list of customer persons related to given params.
     */
    public getCustomerPersons(params: CustomerPersonsParams): Promise<CustomerPersonItem[]> {
        return this.datatask.Post(71, params);
    }

    /**
     * Retrieves list of sellers using the given query params.
     *
     * @datatask 628
     * @datataskName sp_ws_p2_seller_list
     * @param params
     * @returns promise containing list of sellers.
     */
    public getSellers(params: GetSellersParams): Promise<Item[]> {
        return this.datatask.Post(628, params);
    }

    /**
     * Retrieves invoice methods.
     *
     * @datatask 1766
     * @datataskName sp_ws_p2_invoicemethod_select_list
     * @returns promise containing list of invoice methods.
     */
    public getInvoiceMethods(): Promise<InvoiceMethodsItem[]> {
        return this.datatask.Post(1766);
    }

    /**
     * Retrieves list of facotrings.
     *
     * @datatask 625
     * @datataskName sp_ws_p2_factoring_list
     * @returns promise containing list of factorings
     */
    public getFactorings(): Promise<FactoringItem[]> {
        return this.datatask.Post(625);
    }

    /**
     * Retrieves list of different salarytypes.
     *
     * @datatask 481
     * @datataskName sp_ws_p2_salarytype_list
     * @returns promise containing different salarytypes.
     */
    public getSalaryTypes(): Promise<SalaryTypeItem[]> {
        return this.datatask.Post(481);
    }

    /**
     * @datatask 2203
     * @datataskName sp_ws_p2_pricemethod_selectlist
     * @returns promise containing list of price methods.
     */
    public getPriceMethods(): Promise<PriceMethodItem[]> {
        return this.datatask.Post(2203);
    }

    /**
     * @datatask 823
     * @datataskName sp_ws_p2_pricemethod_external_list
     * @returns promise containing list of price methods.
     */
    public getExternalPriceMethods(): Promise<ExternalPriceMethodItem[]> {
        return this.datatask.Post(823);
    }
}

export interface ExternalPriceMethodItem {
    pricemethod_id: string;
    pricemethod_name: string;
    orderby: string;
}

export interface PriceMethodItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface SalaryTypeItem {
    item_id: string;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: string;
}

export interface InvoiceMethodsItem {
    invoicemethod_no: string;
    invoicemethod_name: string;
    orderby: `${number}`;
}

export interface GetSellersParams {
    /**
     * Which businessco to get sellers for, if not given returns list of all sellers.
     */
    businessco_no?: `${number}`;

    /**
     * Whether to add a option signaling choosing all sellers.
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface CustomerPersonsParams {
    cust_no: number | `${number}`;
}

export interface CustomerPersonItem {
    item_id: number | `${number}`;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: number | `${number}`;
    pers_phone: string;
    pers_email: string;
}

export interface TaxParams {
    tax_keyno: string | number;
}

export interface Tax {
    tax_keyno: string | number;
    tax_sting: string;
    tax_pst: string | number;
    tax_field: string;
    accountingcode: string;
    country_id: string;
    accountingcode_in: string;
}

export interface TaxesParams {
    /**
     * @default 'ALL'
     */
    country_id?: string;
}

export interface TaxItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    orderby: string;
    tax_keyno: string;
    tax_sting: string;
}

export interface PriceListsParams {
    /**
     * @default '0'
     */
    factorbased?: '1' | '0';

    /**
     * @default 'ALL'
     */
    pricelist_id?: string;
}

export interface PriceListItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
}

export interface PaymentTypeItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
}

export interface FactoringItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
}

export interface ShiftTypesParams {
    /**
     * @default '0'
     */
    only_active?: '1' | '0';

    /**
     * @default '--'';
     */
    labour_no?: string;
}

export interface ShiftTypeItem {
    item_id: string;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: string;
    background_color_hex: string;
    text_color_hex: string;
    item_validok: '1' | '0';
    item_glyphicon: string;
    item_glyphicon_color: string;
}

/**
 * Represents parameters used for retrieving list of activity statuses.
 */
export interface ActivityStatusesParams {
    /**
     * The id of an activity type to filter the statuses list with, if any.
     */
    activitytype_no?: string | number;

    /**
     * Whether an item for selecting all activity statuses should be added, default `'0'`.
     */
    add_all?: '1' | '0';
}

/**
 * Represents a stockplace item.
 */
export interface ActivityStatusItem {
    /**
     * The id of the activity status.
     */
    actstatus_keyno: string;

    /**
     * The name of the activity status.
     */
    actstatus_name: string;

    /**
     * Whether ok should be set to `'1'` for an activity with this status.
     */
    set_ok: '1' | '0';

    /**
     * The icon representing this activity status.
     */
    glyphicon: string;

    /**
     * The color of the icon representing this activity status.
     */
    glyphicon_color: string;

    /**
     * The order of this item in the list.
     */
    orderby: string | number;
}

/**
 * Represents parameters used to retrieving list of business sites.
 */
export interface BusinessSitesParams {
    /**
     * Business co to filter list of business sites with, if any.
     */
    businessco_no?: string | number;

    /**
     * Whether to add an option for selecting all business sites in the list.
     */
    add_all?: '1' | '0';
}

/**
 * Represents a valuta.
 */
export interface ValutaItem {
    valuta_id: string;
    valuta_name: string;
    valuta_name_with_exchangerate: string;
    valuta_exchangerate: string;
    valuta_exchangefactor: string;
    orderby: string;
}

export interface ActivityTypeParams {
    /**
     * Whether to only find the types which supports time entry.
     */
    only_timeentry: '1' | '0';

    /**
     * Whether to add an item to the list representing all activity types.
     */
    extra_record_for_all: '1' | '0';

    onweb: '1' | '0';
    only_deviation: '1' | '0';
}

export interface ActivityTypeItem {
    activitytype_no?: string;
    activitytype_name?: string;
    orderby?: string;
    item_id?: string;
    item_name?: string;
    item_path?: string;
    item_state?: string;
    item_parms?: string;
    item_glyphicon?: string;
    item_glyphicon_color?: string;
    sync_with_microsoft_365?: '1' | '0';
}
